import { api } from "./client";
import type { PagedResult } from "@/types/api";

// Brokerage Module v1.0 — mirrors PhoenixCapital.Core enums + DTOs. Enums
// serialize as strings (Program.cs JsonStringEnumConverter).

export type BrokerageStatus =
  | "Prospect" | "Applied" | "UnderReview" | "Certified" // onboarding pipeline
  | "Active" // legacy operational (grandfathered, off-board)
  | "Inactive" | "Suspended" | "Declined" | "Archived"; // off-ramps
export type PartnerKind = "Broker" | "Lead";
export type AffiliationType = "Employed" | "Contracted" | "Affiliated" | "Unknown";

// Brokerage Certification & Pipeline — the onboarding funnel columns (mirrors
// the broker pipeline). The Kanban board renders only these four; off-ramp and
// grandfathered Active firms sit off-board.
export const BROKERAGE_PIPELINE_STATUSES: BrokerageStatus[] =
  ["Prospect", "Applied", "UnderReview", "Certified"];
export const BROKERAGE_ARCHIVE_STATUSES: BrokerageStatus[] =
  ["Inactive", "Suspended", "Declined", "Archived"];
export const isBrokerageArchiveStatus = (s: BrokerageStatus) =>
  BROKERAGE_ARCHIVE_STATUSES.includes(s);
// Full set for the list-filter dropdown (includes legacy Active).
export const BROKERAGE_STATUSES: BrokerageStatus[] = [
  "Prospect", "Applied", "UnderReview", "Certified",
  "Active", "Inactive", "Suspended", "Declined", "Archived",
];
export const AFFILIATION_TYPES: AffiliationType[] = ["Employed", "Contracted", "Affiliated", "Unknown"];

// Single source of truth for the status → Badge tone mapping (the two staff
// surfaces share it, so extending BrokerageStatus can't leave one exhaustive
// map out of sync).
export type BrokerageBadgeTone =
  | "draft" | "review" | "finalized" | "archived" | "error" | "info" | "gold";
export const BROKERAGE_STATUS_BADGE: Record<BrokerageStatus, BrokerageBadgeTone> = {
  Prospect: "info",
  Applied: "info",
  UnderReview: "review",
  Certified: "finalized",
  Active: "finalized",
  Inactive: "draft",
  Suspended: "error",
  Declined: "error",
  Archived: "archived",
};

export interface BrokerageSummaryDto {
  id: string;
  name: string;
  dba?: string | null;
  phone?: string | null;
  status: BrokerageStatus;
  brokerCount: number;
  leadCount: number;
  createdAt: string;
  modifiedAt?: string | null;
}

export interface BrokerageDetailDto {
  id: string;
  name: string;
  dba?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  primaryContactName?: string | null;
  notes?: string | null;
  status: BrokerageStatus;
  brokerCount: number;
  leadCount: number;
  createdAt: string;
  modifiedAt?: string | null;
  contacts: BrokerageContactDto[];
  certifiedAt?: string | null;
}

// Brokerage CRM parity — additional firm contacts (mirrors BrokerContactDto).
export interface BrokerageContactDto {
  id: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

export interface BrokerageSearchResultDto {
  id: string;
  name: string;
  dba?: string | null;
  city?: string | null;
  state?: string | null;
  status: BrokerageStatus;
}

export interface UpsertBrokeragePayload {
  name: string;
  dba?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  primaryContactName?: string | null;
  notes?: string | null;
  status?: BrokerageStatus;
  // WS5 — soft, overridable create-time dedup guard (mirrors the vendor create
  // flow). `true` bypasses a Likely/Strong duplicate 409; the optional reason
  // is audited with the override. Ignored on the update path.
  confirmCreateNew?: boolean;
  createNewReason?: string | null;
}

export interface BrokerageListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: BrokerageStatus;
}

// Brokerage / Sub-Broker (PR-3, WS-1) — firm Production tab. Mirrors
// Core/DTOs/Brokers/BrokerageProductionDto.cs. Money fields are decimals.

export interface BrokeragePipelineMixDto {
  stage: string;
  count: number;
}

export interface BrokerageProductionSubBrokerDto {
  subBrokerId: string;
  subBrokerName: string;
  dealCount: number;
  ledgerGross: number;
  /** True when any of this sub-broker's split-ledger rows had no split
   * configured at post time (gross booked before a split existed). */
  anyUnallocated: boolean;
}

export interface BrokerageProductionDto {
  brokerageId: string;
  subBrokerCount: number;
  dealsSourcedCount: number;
  dealsSourcedVolume: number;
  loansOriginatedCount: number;
  loansOriginatedTotal: number;
  activeDealCount: number;
  closedDealCount: number;
  pipelineMix: BrokeragePipelineMixDto[];
  totalGross: number;
  subBrokers: BrokerageProductionSubBrokerDto[];
}

export interface BrokerageAffiliationDto {
  id: string;
  brokerageId: string;
  brokerageName: string;
  partnerId: string;
  partnerKind: PartnerKind;
  partnerName?: string | null;
  affiliationType: AffiliationType;
  isPrimary: boolean;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  linkedDate: string;
  linkedByUserId: string;
  // Lead Conversion Router (WS-3) — the affiliated broker's immutable payee type
  // ("Independent" | "Junior"); null for a Lead partner.
  brokerType?: "Independent" | "Junior" | null;
}

export interface LinkAffiliationPayload {
  brokerageId: string;
  partnerId: string;
  partnerKind: PartnerKind;
  affiliationType?: AffiliationType;
  isPrimary?: boolean;
  startDate?: string | null;
  notes?: string | null;
}

export interface UpdateAffiliationPayload {
  affiliationType: AffiliationType;
  isPrimary: boolean;
  notes?: string | null;
}

export interface EndAffiliationPayload {
  endDate: string;
  reason?: string | null;
}

// Brokerage Certification & Pipeline — pipeline board + advisory stage readiness.
// Mirrors Core/DTOs/Brokers/BrokerageDtos.cs. Enums serialize as strings, so the
// dictionary keys are the BrokerageStatus values.
export interface BrokeragePipelineDto {
  stages: Partial<Record<BrokerageStatus, BrokerageSummaryDto[]>>;
  totalBrokerages: number;
}

export interface BrokerageStageGateDto {
  stageFrom: string | null;
  stageTo: string;
  label: string;
  certified: boolean;
}

export interface BrokerageStageReadinessDto {
  hasContactDetails: boolean;
  hasAddress: boolean;
  hasTaxId: boolean;
  portalAccountProvisioned: boolean;
  hasActiveSubBrokers: boolean;
  stageGates: BrokerageStageGateDto[];
}

/** Portal absorption — the firm's linked portal login id (reverse
 * PortalUser.LinkedBrokerageId lookup); null when no account exists yet. */
export interface BrokerageLinkedPortalAccountDto {
  portalUserId: string | null;
}

/** Result of the brokerage portal invite (mirrors BrokeragePortalInviteResultDto). */
export interface BrokeragePortalInviteResultDto {
  brokerageId: string;
  portalUserId: string;
  email: string;
  isExistingAccount: boolean;
  status: BrokerageStatus;
}

export const brokeragesApi = {
  list: (query?: BrokerageListQuery) =>
    api.get<PagedResult<BrokerageSummaryDto>>("/brokerages", query),
  get: (id: string) => api.get<BrokerageDetailDto>(`/brokerages/${id}`),
  create: (body: UpsertBrokeragePayload) => api.post<BrokerageDetailDto>("/brokerages", body),
  update: (id: string, body: UpsertBrokeragePayload) =>
    api.put<BrokerageDetailDto>(`/brokerages/${id}`, body),
  delete: (id: string) => api.delete<void>(`/brokerages/${id}`),
  archive: (id: string) => api.post<BrokerageDetailDto>(`/brokerages/${id}/archive`),
  search: (q: string, take = 10) =>
    api.get<BrokerageSearchResultDto[]>("/brokerages/search", { q, take }),
  getProduction: (id: string) =>
    api.get<BrokerageProductionDto>(`/brokerages/${id}/production`),
  pipeline: () => api.get<BrokeragePipelineDto>("/brokerages/pipeline"),
  stageReadiness: (id: string) =>
    api.get<BrokerageStageReadinessDto>(`/brokerages/${id}/stage-readiness`),
  // Manual pipeline transition (the stepper + "Change status" control). Fully
  // manual — no prerequisites, no certification gate.
  changeStatus: (id: string, targetStatus: BrokerageStatus, reason?: string) =>
    api.post<BrokerageDetailDto>(`/brokerages/${id}/status`, { targetStatus, reason }),
  // Additional contacts (Brokerage CRM parity; mirrors brokersApi.upsertContact).
  upsertContact: (id: string, contactId: string | undefined, body: {
    firstName: string;
    lastName: string;
    title?: string | null;
    email?: string | null;
    phone?: string | null;
    isPrimary: boolean;
    isActive: boolean;
  }) => api.post<BrokerageContactDto>(
    `/brokerages/${id}/contacts${contactId ? `?contactId=${contactId}` : ""}`, body),
  deleteContact: (id: string, contactId: string) =>
    api.delete<void>(`/brokerages/${id}/contacts/${contactId}`),
  // Portal absorption (WS-D1) — the staff portal affordance on the firm page.
  portalAccount: (id: string) =>
    api.get<BrokerageLinkedPortalAccountDto>(`/brokerages/${id}/portal-account`),
  portalInvite: (id: string) =>
    api.post<BrokeragePortalInviteResultDto>(`/brokerages/${id}/portal-invite`),
};

export const brokerageAffiliationsApi = {
  listForPartner: (partnerId: string, partnerKind: PartnerKind, includeEnded = false) =>
    api.get<BrokerageAffiliationDto[]>("/brokerage-affiliations", { partnerId, partnerKind, includeEnded }),
  listForBrokerage: (brokerageId: string, includeEnded = false) =>
    api.get<BrokerageAffiliationDto[]>("/brokerage-affiliations", { brokerageId, includeEnded }),
  link: (body: LinkAffiliationPayload) =>
    api.post<BrokerageAffiliationDto>("/brokerage-affiliations", body),
  update: (id: string, body: UpdateAffiliationPayload) =>
    api.put<BrokerageAffiliationDto>(`/brokerage-affiliations/${id}`, body),
  setPrimary: (id: string) =>
    api.post<BrokerageAffiliationDto>(`/brokerage-affiliations/${id}/set-primary`),
  clearPrimary: (id: string) =>
    api.post<BrokerageAffiliationDto>(`/brokerage-affiliations/${id}/clear-primary`),
  end: (id: string, body: EndAffiliationPayload) =>
    api.post<BrokerageAffiliationDto>(`/brokerage-affiliations/${id}/end`, body),
  remove: (id: string) => api.delete<void>(`/brokerage-affiliations/${id}`),
};
